import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import PageMeta from "../../components/common/PageMeta";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import Button from "../../components/ui/button/Button";
import { TableSkeleton } from "../../components/ui/skeleton";
import { Modal } from "../../components/ui/modal";
import { useModal } from "../../hooks/useModal";

import { fetchWellsHistory, WellHistory, normalizeApi14 } from "../../lib/api/wells";
import { generatePlansAndWait } from "../../lib/api/bulk";

import {
  createResearchSession,
  getResearchSession,
  getResearchSummary,
  ResearchSession,
  WellSummary,
  createBulkResearchSessions,
  BulkSessionResult,
} from "../../lib/api/research";
import IndexingProgress from "../../components/research/IndexingProgress";
import { useToast } from "../../contexts/ToastContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";

function detectState(apiNumber: string): string | null {
  const digits = apiNumber.replace(/\D/g, '');
  if (digits.startsWith('30')) return 'NM';
  if (digits.startsWith('42')) return 'TX';
  return null;
}

export default function WellRegistry() {
  const navigate = useNavigate();
  const { success, error: showError } = useToast();
  const { currentWorkspaceId } = useWorkspace();
  const [wells, setWells] = useState<WellHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 25;
  const [searchTerm, setSearchTerm] = useState("");
  const [colFilterWell, setColFilterWell] = useState("");
  const [colFilterOperator, setColFilterOperator] = useState("");
  const [colFilterLocation, setColFilterLocation] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);
  const [sortField, setSortField] = useState<"lease_name" | "operator_name" | "county" | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  // Bulk operations state
  const [selectedWells, setSelectedWells] = useState<string[]>([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(0);

  // Add Well modal state (inline research)
  const addWellModal = useModal();
  type AddWellPhase = 'input' | 'progress' | 'complete';
  const [addWellPhase, setAddWellPhase] = useState<AddWellPhase>('input');
  const [addWellApiInput, setAddWellApiInput] = useState('');
  const [addWellDetectedState, setAddWellDetectedState] = useState<string | null>(null);
  const [addWellManualState, setAddWellManualState] = useState<'NM' | 'TX' | null>(null);
  const [addWellSession, setAddWellSession] = useState<ResearchSession | null>(null);
  const [addWellSummary, setAddWellSummary] = useState<WellSummary | null>(null);
  const [addWellStarting, setAddWellStarting] = useState(false);
  const [addWellStartError, setAddWellStartError] = useState<string | null>(null);
  const addWellPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Bulk Add modal state
  const bulkAddModal = useModal();
  type BulkAddPhase = 'input' | 'progress' | 'complete';
  const [bulkAddPhase, setBulkAddPhase] = useState<BulkAddPhase>('input');
  const [bulkApiInput, setBulkApiInput] = useState('');
  const [bulkManualState, setBulkManualState] = useState<'NM' | 'TX' | null>(null);
  const [bulkStarting, setBulkStarting] = useState(false);
  const [bulkStartError, setBulkStartError] = useState<string | null>(null);
  const [csvMixedNote, setCsvMixedNote] = useState<string | null>(null);

  interface BulkWellRow {
    api_number: string;
    session_id: string | null;
    state: string | null;
    status: 'pending' | 'fetching' | 'indexing' | 'ready' | 'error';
    error: string | null;
    total_documents: number;
    indexed_documents: number;
  }
  const [bulkWellRows, setBulkWellRows] = useState<BulkWellRow[]>([]);
  const bulkPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [openDropdownApi, setOpenDropdownApi] = useState<string | null>(null);

  // Fetch wells on component mount and workspace change
  useEffect(() => {
    const loadWells = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetchWellsHistory();
        setWells(response.wells);
      } catch (err) {
        console.error("Error fetching wells:", err);
        setError(
          err instanceof Error
            ? err.message
            : "Failed to load wells. Please try again."
        );
      } finally {
        setLoading(false);
      }
    };

    loadWells();
  }, [currentWorkspaceId]);

  // Filter + sort
  const filteredWells = wells.filter((w) => {
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      const globalMatch =
        w.api14?.toLowerCase().includes(q) ||
        w.lease_name?.toLowerCase().includes(q) ||
        w.operator_name?.toLowerCase().includes(q) ||
        w.county?.toLowerCase().includes(q) ||
        w.state?.toLowerCase().includes(q);
      if (!globalMatch) return false;
    }
    if (colFilterWell.trim()) {
      const q = colFilterWell.toLowerCase();
      const match =
        w.lease_name?.toLowerCase().includes(q) ||
        w.api14?.toLowerCase().includes(q);
      if (!match) return false;
    }
    if (colFilterOperator.trim()) {
      if (!w.operator_name?.toLowerCase().includes(colFilterOperator.toLowerCase())) return false;
    }
    if (colFilterLocation.trim()) {
      const q = colFilterLocation.toLowerCase();
      const match =
        w.county?.toLowerCase().includes(q) ||
        w.state?.toLowerCase().includes(q);
      if (!match) return false;
    }
    return true;
  });

  const sortedWells = sortField
    ? [...filteredWells].sort((a, b) => {
        const av = (a[sortField] ?? "").toLowerCase();
        const bv = (b[sortField] ?? "").toLowerCase();
        const cmp = av.localeCompare(bv);
        return sortDir === "asc" ? cmp : -cmp;
      })
    : filteredWells;

  const handleSortClick = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
    setCurrentPage(1);
  };

  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedWells.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedWells.length / itemsPerPage);

  // Handlers
  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleViewWell = (api14: string) => {
    navigate(`/well/${api14}`);
  };


  // Bulk generate handler
  const handleBulkGenerate = async () => {
    setBulkLoading(true);
    setBulkProgress(0);

    try {
      await generatePlansAndWait(
        { well_ids: selectedWells },
        {
          onProgress: (j) => setBulkProgress(j.progress_percentage || 0)
        }
      );

      success('Plans Generated', `Successfully generated plans for ${selectedWells.length} well${selectedWells.length !== 1 ? 's' : ''}.`);
      setSelectedWells([]);

      // Optionally refresh well list
      const response = await fetchWellsHistory();
      setWells(response.wells);
    } catch (err) {
      console.error("Error generating bulk plans:", err);
      showError('Bulk Operation Failed', err instanceof Error ? err.message : 'Failed to generate plans. Please try again.');
    } finally {
      setBulkLoading(false);
      setBulkProgress(0);
    }
  };

  // Clear selection
  const handleClearSelection = () => {
    setSelectedWells([]);
  };

  // Toggle all wells selection
  const handleToggleAll = (checked: boolean) => {
    if (checked) {
      setSelectedWells(sortedWells.map(w => w.api14));
    } else {
      setSelectedWells([]);
    }
  };

  // Toggle individual well selection
  const handleToggleWell = (api14: string, checked: boolean) => {
    if (checked) {
      setSelectedWells([...selectedWells, api14]);
    } else {
      setSelectedWells(selectedWells.filter(id => id !== api14));
    }
  };

  // Cleanup poll on unmount
  useEffect(() => {
    return () => {
      if (addWellPollRef.current) clearInterval(addWellPollRef.current);
      if (bulkPollRef.current) clearInterval(bulkPollRef.current);
    };
  }, []);

  // Close filter panel on outside click
  useEffect(() => {
    if (!filterOpen) return;
    const handler = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [filterOpen]);

  const startAddWellPolling = (sessionId: string) => {
    if (addWellPollRef.current) clearInterval(addWellPollRef.current);
    addWellPollRef.current = setInterval(async () => {
      try {
        const updated = await getResearchSession(sessionId);
        setAddWellSession(updated);
        if (updated.status === 'ready') {
          clearInterval(addWellPollRef.current!);
          addWellPollRef.current = null;
          getResearchSummary(sessionId).then(setAddWellSummary).catch(console.error);
          fetchWellsHistory().then(r => setWells(r.wells)).catch(console.error);
          setAddWellPhase('complete');
        } else if (updated.status === 'error') {
          clearInterval(addWellPollRef.current!);
          addWellPollRef.current = null;
        }
      } catch (err) { console.error('[AddWell] Poll error:', err); }
    }, 2000);
  };

  const handleAddWellStart = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = addWellApiInput.trim();
    if (!trimmed) return;
    const resolvedState = (addWellDetectedState ?? addWellManualState) ?? undefined;
    setAddWellStarting(true);
    setAddWellStartError(null);
    try {
      const session = await createResearchSession(trimmed, resolvedState ?? undefined);
      setAddWellSession(session);
      if (session.status === 'ready') {
        const summary = await getResearchSummary(session.id).catch(() => null);
        setAddWellSummary(summary);
        fetchWellsHistory().then(r => setWells(r.wells)).catch(console.error);
        setAddWellPhase('complete');
      } else {
        setAddWellPhase('progress');
        startAddWellPolling(session.id);
      }
    } catch (err) {
      setAddWellStartError(err instanceof Error ? err.message : 'Failed to start research');
    } finally {
      setAddWellStarting(false);
    }
  };

  const startBulkPolling = () => {
    if (bulkPollRef.current) clearInterval(bulkPollRef.current);

    bulkPollRef.current = setInterval(async () => {
      let currentActiveRows: BulkWellRow[] = [];

      setBulkWellRows(prev => {
        currentActiveRows = prev.filter(
          r => r.session_id !== null && r.status !== 'ready' && r.status !== 'error'
        );
        return prev;
      });

      if (currentActiveRows.length === 0) {
        clearInterval(bulkPollRef.current!);
        bulkPollRef.current = null;
        return;
      }

      const updates = await Promise.allSettled(
        currentActiveRows.map(r => getResearchSession(r.session_id!))
      );

      setBulkWellRows(prev => {
        const next = [...prev];
        updates.forEach((result, idx) => {
          const sessionId = currentActiveRows[idx].session_id;
          const rowIdx = next.findIndex(r => r.session_id === sessionId);
          if (rowIdx === -1) return;
          if (result.status === 'fulfilled') {
            const sess = result.value;
            next[rowIdx] = {
              ...next[rowIdx],
              status: sess.status as BulkWellRow['status'],
              total_documents: sess.total_documents,
              indexed_documents: sess.indexed_documents,
            };
          }
        });

        const allDone = next.every(r => r.status === 'ready' || r.status === 'error');
        if (allDone) {
          clearInterval(bulkPollRef.current!);
          bulkPollRef.current = null;
          fetchWellsHistory().then(r => setWells(r.wells)).catch(console.error);
          setBulkAddPhase('complete');
        }

        return next;
      });
    }, 2000);
  };

  const handleBulkAddStart = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = [...new Set(
      bulkApiInput.split(/[\n,;\s]+/).map(s => s.trim()).filter(s => s.length > 0)
    )];
    if (parsed.length === 0) { setBulkStartError('Enter at least one API number.'); return; }
    if (parsed.length > 50) { setBulkStartError(`Maximum 50 wells per batch (you entered ${parsed.length}).`); return; }

    setBulkStarting(true);
    setBulkStartError(null);

    try {
      const response = await createBulkResearchSessions(parsed, bulkManualState ?? undefined);
      const initialRows: BulkWellRow[] = response.sessions.map((s: BulkSessionResult) => ({
        api_number: s.api_number,
        session_id: s.session_id,
        state: s.state,
        status: s.status === 'error' ? 'error' : 'pending',
        error: s.error,
        total_documents: 0,
        indexed_documents: 0,
      }));
      setBulkWellRows(initialRows);
      setBulkAddPhase('progress');
      const hasPending = initialRows.some(r => r.session_id !== null && r.status !== 'error');
      if (hasPending) {
        startBulkPolling();
      } else {
        setBulkAddPhase('complete');
      }
    } catch (err) {
      setBulkStartError(err instanceof Error ? err.message : 'Failed to start bulk research.');
    } finally {
      setBulkStarting(false);
    }
  };

  const handleCloseBulkAddModal = () => {
    if (bulkPollRef.current) { clearInterval(bulkPollRef.current); bulkPollRef.current = null; }
    bulkAddModal.closeModal();
    setTimeout(() => {
      setBulkAddPhase('input');
      setBulkApiInput('');
      setBulkManualState(null);
      setCsvMixedNote(null);
      setBulkStarting(false);
      setBulkStartError(null);
      setBulkWellRows([]);
    }, 0);
  };

  const handleCloseAddWellModal = () => {
    if (addWellPollRef.current) { clearInterval(addWellPollRef.current); addWellPollRef.current = null; }
    addWellModal.closeModal();
    setTimeout(() => {
      setAddWellPhase('input');
      setAddWellApiInput('');
      setAddWellDetectedState(null);
      setAddWellManualState(null);
      setAddWellSession(null);
      setAddWellSummary(null);
      setAddWellStarting(false);
      setAddWellStartError(null);
    }, 0);
  };

  return (
    <>
      <PageMeta
        title="Well Registry | RegulAgent"
        description="View and manage well registry"
      />
      
      <PageBreadcrumb pageTitle="Well Registry" />

      <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
        <div className="px-6 py-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                All Wells
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {searchTerm ? `${sortedWells.length} of ${wells.length} wells` : `${wells.length} wells`}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                size="sm"
                onClick={addWellModal.openModal}
              >
                + Add Well
              </Button>
              <Button size="sm" variant="outline" onClick={bulkAddModal.openModal}>
                Bulk Add
              </Button>
              {/* Filter button */}
              <div className="relative" ref={filterRef}>
                <button
                  type="button"
                  onClick={() => setFilterOpen((o) => !o)}
                  className={`inline-flex items-center gap-2 rounded-lg border px-4 py-3 text-theme-sm font-medium shadow-theme-xs transition-colors ${
                    colFilterWell || colFilterOperator || colFilterLocation
                      ? "border-brand-400 bg-brand-50 text-brand-700 dark:border-brand-500 dark:bg-brand-500/10 dark:text-brand-300"
                      : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-white/[0.03]"
                  }`}
                >
                  <svg className="stroke-current fill-white dark:fill-gray-800" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.29004 5.90393H17.7067" stroke="" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M17.7075 14.0961H2.29085" stroke="" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12.0826 3.33331C13.5024 3.33331 14.6534 4.48431 14.6534 5.90414C14.6534 7.32398 13.5024 8.47498 12.0826 8.47498C10.6627 8.47498 9.51172 7.32398 9.51172 5.90415C9.51172 4.48432 10.6627 3.33331 12.0826 3.33331Z" fill="" stroke="" strokeWidth="1.5"/>
                    <path d="M7.91745 11.525C6.49762 11.525 5.34662 12.676 5.34662 14.0959C5.34661 15.5157 6.49762 16.6667 7.91745 16.6667C9.33728 16.6667 10.4883 15.5157 10.4883 14.0959C10.4883 12.676 9.33728 11.525 7.91745 11.525Z" fill="" stroke="" strokeWidth="1.5"/>
                  </svg>
                  Filter
                  {(colFilterWell || colFilterOperator || colFilterLocation) && (
                    <span className="flex h-4 w-4 items-center justify-center rounded-full bg-brand-500 text-[10px] font-bold text-white">
                      {[colFilterWell, colFilterOperator, colFilterLocation].filter(Boolean).length}
                    </span>
                  )}
                </button>

                {filterOpen && (
                  <div className="absolute right-0 top-full z-50 mt-2 w-64 rounded-xl border border-gray-200 bg-white p-4 shadow-lg dark:border-gray-700 dark:bg-gray-900">
                    <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">Filter by</p>
                    <div className="space-y-3">
                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Well / API</label>
                        <input
                          type="text"
                          placeholder="Lease name or API..."
                          value={colFilterWell}
                          onChange={(e) => { setColFilterWell(e.target.value); setCurrentPage(1); }}
                          className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:border-brand-400 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Operator</label>
                        <input
                          type="text"
                          placeholder="Operator name..."
                          value={colFilterOperator}
                          onChange={(e) => { setColFilterOperator(e.target.value); setCurrentPage(1); }}
                          className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:border-brand-400 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Location</label>
                        <input
                          type="text"
                          placeholder="County or state..."
                          value={colFilterLocation}
                          onChange={(e) => { setColFilterLocation(e.target.value); setCurrentPage(1); }}
                          className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:border-brand-400 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
                        />
                      </div>
                    </div>
                    {(colFilterWell || colFilterOperator || colFilterLocation) && (
                      <button
                        type="button"
                        onClick={() => { setColFilterWell(""); setColFilterOperator(""); setColFilterLocation(""); setCurrentPage(1); }}
                        className="mt-3 w-full rounded-lg border border-gray-200 py-1.5 text-xs font-medium text-gray-500 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-white/[0.03]"
                      >
                        Clear filters
                      </button>
                    )}
                  </div>
                )}
              </div>

              <form>
                <div className="relative">
                  <button className="absolute -translate-y-1/2 left-4 top-1/2">
                    <svg className="fill-gray-500 dark:fill-gray-400" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" clipRule="evenodd" d="M3.04199 9.37381C3.04199 5.87712 5.87735 3.04218 9.37533 3.04218C12.8733 3.04218 15.7087 5.87712 15.7087 9.37381C15.7087 12.8705 12.8733 15.7055 9.37533 15.7055C5.87735 15.7055 3.04199 12.8705 3.04199 9.37381ZM9.37533 1.54218C5.04926 1.54218 1.54199 5.04835 1.54199 9.37381C1.54199 13.6993 5.04926 17.2055 9.37533 17.2055C11.2676 17.2055 13.0032 16.5346 14.3572 15.4178L17.1773 18.2381C17.4702 18.531 17.945 18.5311 18.2379 18.2382C18.5308 17.9453 18.5309 17.4704 18.238 17.1775L15.4182 14.3575C16.5367 13.0035 17.2087 11.2671 17.2087 9.37381C17.2087 5.04835 13.7014 1.54218 9.37533 1.54218Z" fill=""/>
                    </svg>
                  </button>
                  <input
                    type="text"
                    placeholder="Search by API, lease, operator, location..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    className="h-[42px] w-full rounded-lg border border-gray-300 bg-transparent py-2.5 pl-[42px] pr-4 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800 xl:w-[300px]"
                  />
                </div>
              </form>
            </div>
          </div>
        </div>

        {loading && (
          <div className="overflow-hidden">
            <div className="max-w-full overflow-x-auto">
              <TableSkeleton rows={10} columns={8} />
            </div>
          </div>
        )}

        {error && (
          <div className="p-6">
            <div className="rounded-lg bg-error-50 p-4 dark:bg-error-500/15">
              <p className="text-sm font-medium text-error-700 dark:text-error-400">
                {error}
              </p>
            </div>
          </div>
        )}

        {!loading && !error && wells.length === 0 && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <p className="text-gray-600 dark:text-gray-400">
                No wells found
              </p>
            </div>
          </div>
        )}

        {!loading && !error && wells.length > 0 && (
          <>
            {/* Bulk Actions Toolbar */}
            {selectedWells.length > 0 && (
              <div className="px-6 py-4 bg-brand-50 dark:bg-brand-500/10 border-b border-gray-200 dark:border-white/[0.05]">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-brand-700 dark:text-brand-300">
                      {selectedWells.length} well{selectedWells.length !== 1 ? 's' : ''} selected
                    </span>
                    {bulkLoading && (
                      <div className="flex items-center gap-2">
                        <div className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-brand-500 border-r-transparent"></div>
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {bulkProgress > 0 ? `${bulkProgress}%` : 'Processing...'}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={handleBulkGenerate}
                      disabled={bulkLoading}
                      size="sm"
                    >
                      Generate Plans
                    </Button>
                    <Button
                      onClick={handleClearSelection}
                      disabled={bulkLoading}
                      variant="outline"
                      size="sm"
                    >
                      Clear Selection
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <div className="overflow-hidden">
              <div className="max-w-full overflow-x-auto">
                <Table>
                  <TableHeader className="border-gray-100 border-y dark:border-white/[0.05]">
                    {/* Column label row */}
                    <TableRow>
                      <TableCell isHeader className="py-3 px-6 font-normal text-gray-500 text-start text-theme-sm dark:text-gray-400">
                        <input
                          type="checkbox"
                          checked={selectedWells.length === sortedWells.length && sortedWells.length > 0}
                          onChange={(e) => handleToggleAll(e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                        />
                      </TableCell>
                      <TableCell isHeader className="px-6 py-3 font-normal text-gray-500 text-start text-theme-sm dark:text-gray-400">
                        <button type="button" onClick={() => handleSortClick("lease_name")} className="inline-flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">
                          Well
                          <span className="text-xs opacity-40">{sortField === "lease_name" ? (sortDir === "asc" ? "▲" : "▼") : "⇅"}</span>
                        </button>
                      </TableCell>
                      <TableCell isHeader className="px-6 py-3 font-normal text-gray-500 text-start text-theme-sm dark:text-gray-400">
                        <button type="button" onClick={() => handleSortClick("operator_name")} className="inline-flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">
                          Operator
                          <span className="text-xs opacity-40">{sortField === "operator_name" ? (sortDir === "asc" ? "▲" : "▼") : "⇅"}</span>
                        </button>
                      </TableCell>
                      <TableCell isHeader className="px-6 py-3 font-normal text-gray-500 text-start text-theme-sm dark:text-gray-400">
                        <button type="button" onClick={() => handleSortClick("county")} className="inline-flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">
                          Location
                          <span className="text-xs opacity-40">{sortField === "county" ? (sortDir === "asc" ? "▲" : "▼") : "⇅"}</span>
                        </button>
                      </TableCell>
                      <TableCell isHeader className="px-6 py-3 font-normal text-gray-500 text-start text-theme-sm dark:text-gray-400">
                        <span className="sr-only">Action</span>
                      </TableCell>
                      <TableCell isHeader className="px-6 py-3 font-normal text-gray-500 text-end text-theme-sm dark:text-gray-400">
                        <span className="sr-only">View</span>
                      </TableCell>
                    </TableRow>
                  </TableHeader>
                  <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                    {currentItems.map((well) => (
                      <TableRow
                        key={well.id}
                        className="cursor-pointer hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors"
                        onClick={() => navigate(`/well/${well.api14}`)}
                      >
                        <TableCell className="px-6 py-4">
                          <input
                            type="checkbox"
                            checked={selectedWells.includes(well.api14)}
                            onChange={(e) => handleToggleWell(well.api14, e.target.checked)}
                            onClick={(e) => e.stopPropagation()}
                            className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                          />
                        </TableCell>
                        <TableCell className="px-6 py-4 text-gray-700 text-theme-sm dark:text-gray-400">
                          <div className="flex flex-col" onClick={() => handleViewWell(well.api14)}>
                            <span className="font-medium text-gray-800 dark:text-gray-300">
                              {well.lease_name} #{well.well_number}
                            </span>
                            <span className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                              {well.api14?.slice(0, 10)}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="px-6 py-4 text-gray-700 text-theme-sm dark:text-gray-400">
                          <span className="inline-block truncate max-w-[150px]" onClick={() => handleViewWell(well.api14)}>
                            {well.operator_name}
                          </span>
                        </TableCell>
                        <TableCell className="px-6 py-4 text-gray-700 text-theme-sm dark:text-gray-400">
                          <span className="text-xs">
                            {well.county || "Unknown"}, {well.state}
                          </span>
                        </TableCell>
                        <TableCell className="px-6 py-4 text-gray-700 text-theme-sm dark:text-gray-400">
                          <div className="relative inline-block" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => setOpenDropdownApi(openDropdownApi === well.api14 ? null : well.api14)}
                              className="text-gray-500 dark:text-gray-400"
                            >
                              <svg
                                className="fill-current"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  fillRule="evenodd"
                                  clipRule="evenodd"
                                  d="M5.99902 10.245C6.96552 10.245 7.74902 11.0285 7.74902 11.995V12.005C7.74902 12.9715 6.96552 13.755 5.99902 13.755C5.03253 13.755 4.24902 12.9715 4.24902 12.005V11.995C4.24902 11.0285 5.03253 10.245 5.99902 10.245ZM17.999 10.245C18.9655 10.245 19.749 11.0285 19.749 11.995V12.005C19.749 12.9715 18.9655 13.755 17.999 13.755C17.0325 13.755 16.249 12.9715 16.249 12.005V11.995C16.249 11.0285 17.0325 10.245 17.999 10.245ZM13.749 11.995C13.749 11.0285 12.9655 10.245 11.999 10.245C11.0325 10.245 10.249 11.0285 10.249 11.995V12.005C10.249 12.9715 11.0325 13.755 11.999 13.755C12.9655 13.755 13.749 12.9715 13.749 12.005V11.995Z"
                                  fill=""
                                />
                              </svg>
                            </button>
                            <div
                              className={`absolute right-0 z-10 mt-1 w-40 p-2 bg-white border border-gray-200 rounded-2xl shadow-lg dark:border-gray-800 dark:bg-gray-900 transition-opacity ${openDropdownApi === well.api14 ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
                            >
                              <div
                                className="space-y-1"
                                role="menu"
                                aria-orientation="vertical"
                                aria-labelledby="options-menu"
                              >
                                <button
                                  onClick={() => navigate(`/research?api14=${well.api14}`)}
                                  className="text-xs flex w-full rounded-lg px-3 py-2 text-left font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300"
                                >
                                  Research Well
                                </button>
                                <button
                                  onClick={() => navigate(`/filings/new?api14=${well.api14}`)}
                                  className="text-xs flex w-full rounded-lg px-3 py-2 text-left font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300"
                                >
                                  Generate Filing
                                </button>
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="px-6 py-4 text-end">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleViewWell(well.api14); }}
                            className="text-sm font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300"
                          >
                            View Details
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Pagination Controls */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-white/[0.05]">
              <div className="flex items-center justify-between">
                {/* Previous Button */}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <svg
                    className="fill-current"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M2.58301 9.99868C2.58272 10.1909 2.65588 10.3833 2.80249 10.53L7.79915 15.5301C8.09194 15.8231 8.56682 15.8233 8.85981 15.5305C9.15281 15.2377 9.15297 14.7629 8.86018 14.4699L5.14009 10.7472L16.6675 10.7472C17.0817 10.7472 17.4175 10.4114 17.4175 9.99715C17.4175 9.58294 17.0817 9.24715 16.6675 9.24715L5.14554 9.24715L8.86017 5.53016C9.15297 5.23717 9.15282 4.7623 8.85983 4.4695C8.56684 4.1767 8.09197 4.17685 7.79917 4.46984L2.84167 9.43049C2.68321 9.568 2.58301 9.77087 2.58301 9.99715C2.58301 9.99766 2.58301 9.99817 2.58301 9.99868Z"
                      fill=""
                    />
                  </svg>
                  <span className="hidden sm:inline">Previous</span>
                </Button>

                {/* Page Info */}
                <span className="block text-sm font-medium text-gray-700 dark:text-gray-400 sm:hidden">
                  Page {currentPage} of {totalPages}
                </span>

                {/* Page Numbers */}
                <ul className="hidden items-center gap-0.5 sm:flex">
                  {Array.from({ length: totalPages }).map((_, idx) => (
                    <li key={idx}>
                      <button
                        onClick={() => goToPage(idx + 1)}
                        className={`flex h-10 w-10 items-center justify-center rounded-lg text-theme-sm font-medium ${
                          currentPage === idx + 1
                            ? "bg-brand-500 text-white"
                            : "text-gray-700 hover:bg-brand-500/[0.08] dark:hover:bg-brand-500 dark:hover:text-white hover:text-brand-500 dark:text-gray-400"
                        }`}
                      >
                        {idx + 1}
                      </button>
                    </li>
                  ))}
                </ul>

                {/* Next Button */}
                <Button
                  onClick={() => goToPage(currentPage + 1)}
                  size="sm"
                  variant="outline"
                  disabled={currentPage === totalPages}
                >
                  <span className="hidden sm:inline">Next</span>
                  <svg
                    className="fill-current"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M17.4175 9.9986C17.4178 10.1909 17.3446 10.3832 17.198 10.53L12.2013 15.5301C11.9085 15.8231 11.4337 15.8233 11.1407 15.5305C10.8477 15.2377 10.8475 14.7629 11.1403 14.4699L14.8604 10.7472L3.33301 10.7472C2.91879 10.7472 2.58301 10.4114 2.58301 9.99715C2.58301 9.58294 2.91879 9.24715 3.33301 9.24715L14.8549 9.24715L11.1403 5.53016C10.8475 5.23717 10.8477 4.7623 11.1407 4.4695C11.4336 4.1767 11.9085 4.17685 12.2013 4.46984L17.1588 9.43049C17.3173 9.568 17.4175 9.77087 17.4175 9.99715C17.4175 9.99763 17.4175 9.99812 17.4175 9.9986Z"
                      fill=""
                    />
                  </svg>
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add Well Modal (inline research) */}
      <Modal isOpen={addWellModal.isOpen} onClose={handleCloseAddWellModal} className="max-w-[540px] p-5 lg:p-8">

        {/* Phase 1: Input */}
        {addWellPhase === 'input' && (
          <form onSubmit={handleAddWellStart}>
            <h4 className="mb-1 text-lg font-semibold text-gray-800 dark:text-white/90">Add Well</h4>
            <p className="mb-5 text-sm text-gray-500 dark:text-gray-400">
              Enter an API number to research and add the well. <span className="text-gray-400">30-xxx = New Mexico · 42-xxx = Texas</span>
            </p>

            <div className="relative">
              <input
                autoFocus
                value={addWellApiInput}
                onChange={(e) => {
                  setAddWellApiInput(e.target.value);
                  setAddWellDetectedState(detectState(e.target.value));
                  setAddWellManualState(null);
                }}
                placeholder="e.g. 30-015-28692 or 42-123-45678"
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 pr-16 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
              />
              {addWellDetectedState && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-brand-100 px-2 py-0.5 text-xs font-semibold text-brand-700 dark:bg-brand-500/20 dark:text-brand-300">
                  {addWellDetectedState}
                </span>
              )}
            </div>

            {addWellDetectedState === null && addWellApiInput.replace(/\D/g, '').length >= 8 && (
              <div className="mt-3 flex gap-2">
                <p className="mr-1 self-center text-xs text-gray-500">Select state:</p>
                {(['NM', 'TX'] as const).map(s => (
                  <button key={s} type="button" onClick={() => setAddWellManualState(s)}
                    className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
                      addWellManualState === s
                        ? 'border-brand-500 bg-brand-500 text-white'
                        : 'border-gray-300 text-gray-600 hover:border-brand-400 dark:border-gray-600 dark:text-gray-300'
                    }`}>{s}</button>
                ))}
              </div>
            )}

            {addWellStartError && (
              <p className="mt-3 text-sm text-error-600 dark:text-error-400">{addWellStartError}</p>
            )}

            <div className="mt-6 flex gap-3">
              <button
                type="submit"
                disabled={addWellStarting || !addWellApiInput.trim() || (addWellDetectedState === null && addWellManualState === null)}
                className="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {addWellStarting ? (
                  <>
                    <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-r-transparent" />
                    Starting...
                  </>
                ) : 'Research & Add'}
              </button>
              <button type="button" onClick={handleCloseAddWellModal}
                className="rounded-lg border border-gray-300 px-6 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-900">
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Phase 2: Progress */}
        {addWellPhase === 'progress' && (
          <div>
            <h4 className="mb-1 text-lg font-semibold text-gray-800 dark:text-white/90">
              Researching {addWellApiInput}
            </h4>
            <p className="mb-5 text-sm text-gray-500 dark:text-gray-400">
              Fetching and indexing regulatory documents&hellip;
            </p>
            <IndexingProgress
              total={addWellSession?.total_documents ?? 0}
              indexed={addWellSession?.indexed_documents ?? 0}
              failed={addWellSession?.failed_documents ?? 0}
              status={addWellSession?.status ?? 'pending'}
              failedDetails={addWellSession?.failed_document_details}
            />
            {addWellSession?.status === 'error' && (
              <p className="mt-4 text-sm text-error-600 dark:text-error-400">
                {addWellSession.error_message || 'Research failed. Please try again.'}
              </p>
            )}
            {addWellSession?.status === 'error' && (
              <div className="mt-4 flex flex-wrap gap-3">
                <button
                  onClick={() => { navigate('/well/' + normalizeApi14(addWellApiInput)); handleCloseAddWellModal(); }}
                  className="rounded-lg bg-brand-500 px-5 py-2 text-sm font-medium text-white hover:bg-brand-600"
                >
                  View Well &amp; Upload Docs
                </button>
                <button onClick={() => setAddWellPhase('input')}
                  className="rounded-lg border border-gray-300 px-5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300">
                  Try Again
                </button>
                <button onClick={handleCloseAddWellModal}
                  className="rounded-lg border border-gray-300 px-5 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400">
                  Close
                </button>
              </div>
            )}
          </div>
        )}

        {/* Phase 3: Complete */}
        {addWellPhase === 'complete' && (
          <div>
            <div className="mb-4 flex items-center gap-2">
              <svg className="h-5 w-5 text-success-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <h4 className="text-lg font-semibold text-gray-800 dark:text-white/90">Well Added</h4>
            </div>

            {addWellSummary ? (
              <div className="grid grid-cols-2 gap-3 rounded-xl border border-gray-200 p-4 text-sm dark:border-gray-700">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Operator</p>
                  <p className="font-medium text-gray-800 dark:text-gray-200">{addWellSummary.well_info?.operator || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">County</p>
                  <p className="font-medium text-gray-800 dark:text-gray-200">{addWellSummary.well_info?.county || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Field</p>
                  <p className="font-medium text-gray-800 dark:text-gray-200">{addWellSummary.well_info?.field || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Total Depth</p>
                  <p className="font-medium text-gray-800 dark:text-gray-200">
                    {addWellSummary.well_info?.total_depth_ft ? `${addWellSummary.well_info.total_depth_ft.toLocaleString()} ft` : '—'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="animate-pulse h-20 rounded-xl bg-gray-100 dark:bg-gray-800" />
            )}

            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
              {addWellSession?.indexed_documents ?? 0} of {addWellSession?.total_documents ?? 0} documents indexed
            </p>

            {addWellSession?.failed_document_details && addWellSession.failed_document_details.length > 0 && (
              <ul className="mt-2 space-y-1 text-sm text-amber-600 dark:text-amber-400">
                {addWellSession.failed_document_details.map((d) => (
                  <li key={d.filename}>
                    {d.message}: <span className="font-medium break-all">{d.filename}</span>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-6 flex gap-3">
              <button
                onClick={() => { navigate('/research/' + addWellSession!.id); handleCloseAddWellModal(); }}
                className="rounded-lg bg-brand-500 px-6 py-2 text-sm font-medium text-white hover:bg-brand-600 transition-colors"
              >
                View Research
              </button>
              <button onClick={handleCloseAddWellModal}
                className="rounded-lg border border-gray-300 px-6 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-900">
                Done
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Bulk Add Modal */}
      <Modal
        isOpen={bulkAddModal.isOpen}
        onClose={handleCloseBulkAddModal}
        className="max-w-[640px] p-5 lg:p-8"
      >
        {/* Phase 1: Input */}
        {bulkAddPhase === 'input' && (
          <form onSubmit={handleBulkAddStart}>
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h4 className="mb-1 text-lg font-semibold text-gray-800 dark:text-white/90">
                  Bulk Add Wells
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Paste API numbers or upload a CSV file. Maximum 50 wells per batch.
                  <br />
                  <span className="text-xs text-gray-400">30-xxx = New Mexico · 42-xxx = Texas</span>
                </p>
              </div>
              <div>
                <input
                  type="file"
                  accept=".csv,.txt"
                  id="bulk-csv-upload"
                  className="sr-only"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = (ev) => {
                      const text = ev.target?.result as string;
                      const apis = text
                        .split(/[\r\n,;|\t]+/)
                        .map(cell => cell.trim().replace(/[^0-9\-]/g, ''))
                        .filter(v => v.replace(/\D/g, '').length >= 8);
                      setBulkApiInput(apis.join('\n'));
                      const hasNM = apis.some(a => a.replace(/\D/g, '').startsWith('30'));
                      const hasTX = apis.some(a => a.replace(/\D/g, '').startsWith('42'));
                      if (hasNM && hasTX) {
                        setBulkManualState(null);
                        setCsvMixedNote('Mixed NM and TX wells detected — state override reset to Auto.');
                      } else {
                        setCsvMixedNote(null);
                      }
                    };
                    reader.readAsText(file);
                    e.target.value = '';
                  }}
                />
                <label
                  htmlFor="bulk-csv-upload"
                  className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-600 shadow-theme-xs hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-white/[0.03]"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1M8 12l4-4m0 0l4 4m-4-4v12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Upload CSV
                </label>
              </div>
            </div>
            <textarea
              autoFocus
              value={bulkApiInput}
              onChange={e => setBulkApiInput(e.target.value)}
              placeholder={"42-123-45678\n30-015-28692\n42-987-65432"}
              rows={6}
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800 resize-none"
            />
            {csvMixedNote && (
              <div className="mt-2 flex items-start gap-2 rounded-lg bg-info-50 px-3 py-2 text-xs text-info-700 dark:bg-info-500/10 dark:text-info-300">
                <svg className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                {csvMixedNote}
              </div>
            )}
            <div className="mt-3 flex items-center gap-2">
              <p className="text-xs text-gray-500 mr-1">State override:</p>
              {(['NM', 'TX', null] as const).map(s => (
                <button
                  key={String(s)}
                  type="button"
                  onClick={() => { setBulkManualState(s); setCsvMixedNote(null); }}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                    bulkManualState === s
                      ? 'border-brand-500 bg-brand-500 text-white'
                      : 'border-gray-300 text-gray-600 hover:border-brand-400 dark:border-gray-600 dark:text-gray-300'
                  }`}
                >
                  {s ?? 'Auto'}
                </button>
              ))}
            </div>
            {bulkStartError && (
              <p className="mt-3 text-sm text-error-600 dark:text-error-400">{bulkStartError}</p>
            )}
            <div className="mt-6 flex gap-3">
              <button
                type="submit"
                disabled={bulkStarting || !bulkApiInput.trim()}
                className="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-6 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {bulkStarting ? (
                  <>
                    <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-r-transparent" />
                    Starting...
                  </>
                ) : 'Research & Add All'}
              </button>
              <button
                type="button"
                onClick={handleCloseBulkAddModal}
                className="rounded-lg border border-gray-300 px-6 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-900"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Phase 2: Progress */}
        {bulkAddPhase === 'progress' && (
          <div>
            <h4 className="mb-1 text-lg font-semibold text-gray-800 dark:text-white/90">Researching Wells</h4>
            <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
              {bulkWellRows.filter(r => r.status === 'ready').length} of {bulkWellRows.length} complete
            </p>
            <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
              {bulkWellRows.map((row, idx) => (
                <div key={row.session_id ?? `err-${idx}`} className="flex items-center gap-3 rounded-lg border border-gray-100 px-4 py-3 dark:border-white/[0.05]">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{row.api_number}</p>
                    {row.state && <p className="text-xs text-gray-400">{row.state}</p>}
                    {row.status === 'error' && (
                      <p className="text-xs text-error-600 dark:text-error-400 mt-0.5">
                        {row.error || 'Research failed'}
                      </p>
                    )}
                  </div>
                  {row.session_id && row.status !== 'error' && (
                    <div className="w-32 flex-shrink-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-xs font-medium ${
                          row.status === 'ready' ? 'text-success-600' : 'text-brand-600'
                        }`}>
                          {row.status === 'ready' ? 'Ready' :
                           row.status === 'fetching' ? 'Fetching...' :
                           row.status === 'indexing' && row.total_documents > 0
                             ? `${Math.round(row.indexed_documents / row.total_documents * 100)}%`
                             : 'Pending...'}
                        </span>
                      </div>
                      <div className="h-1 w-full rounded-full bg-gray-100 dark:bg-gray-800">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${
                            row.status === 'ready' ? 'bg-success-500' : 'bg-brand-500'
                          }`}
                          style={{
                            width: row.status === 'ready' ? '100%' :
                                   row.total_documents > 0
                                     ? `${Math.round(row.indexed_documents / row.total_documents * 100)}%`
                                     : '5%'
                          }}
                        />
                      </div>
                    </div>
                  )}
                  {row.status === 'error' && !row.session_id && (
                    <span className="flex-shrink-0 rounded-full bg-error-50 px-2.5 py-0.5 text-xs font-medium text-error-700 dark:bg-error-500/15 dark:text-error-400">
                      Error
                    </span>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-5">
              <button
                onClick={handleCloseBulkAddModal}
                className="rounded-lg border border-gray-300 px-6 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-900"
              >
                Close (runs in background)
              </button>
            </div>
          </div>
        )}

        {/* Phase 3: Complete */}
        {bulkAddPhase === 'complete' && (
          <div>
            <div className="mb-4 flex items-center gap-2">
              <svg className="h-5 w-5 text-success-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <h4 className="text-lg font-semibold text-gray-800 dark:text-white/90">Bulk Research Complete</h4>
            </div>
            <div className="grid grid-cols-3 gap-3 rounded-xl border border-gray-200 p-4 text-center dark:border-gray-700 mb-4">
              <div>
                <p className="text-2xl font-bold text-success-600">{bulkWellRows.filter(r => r.status === 'ready').length}</p>
                <p className="text-xs text-gray-500">Ready</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-error-600">{bulkWellRows.filter(r => r.status === 'error').length}</p>
                <p className="text-xs text-gray-500">Failed</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-700 dark:text-gray-300">{bulkWellRows.length}</p>
                <p className="text-xs text-gray-500">Total</p>
              </div>
            </div>
            {bulkWellRows.filter(r => r.status === 'error').length > 0 && (
              <div className="mb-4 rounded-lg bg-error-50 dark:bg-error-500/10 p-3">
                <p className="text-xs font-medium text-error-700 dark:text-error-400 mb-2">Failed wells:</p>
                {bulkWellRows.filter(r => r.status === 'error').map((row, idx) => (
                  <div key={idx} className="flex items-start justify-between gap-2 mb-1.5">
                    <p className="text-xs text-error-600 dark:text-error-400">
                      {row.api_number}: {row.error || 'unknown error'}
                    </p>
                    <button
                      onClick={() => { navigate('/well/' + normalizeApi14(row.api_number)); handleCloseBulkAddModal(); }}
                      className="shrink-0 text-xs font-medium text-brand-600 underline whitespace-nowrap hover:text-brand-700"
                    >
                      View Well &amp; Upload Docs
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => { navigate('/well-registry'); handleCloseBulkAddModal(); }}
                className="rounded-lg bg-brand-500 px-6 py-2 text-sm font-medium text-white hover:bg-brand-600"
              >
                View Registry
              </button>
              <button
                onClick={handleCloseBulkAddModal}
                className="rounded-lg border border-gray-300 px-6 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-900"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </Modal>

    </>
  );
}
